import { FormEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Dialog, Heading, Paragraph } from '@digdir/designsystemet-react';
import type { Proposal } from '../../../api/workEnvironmentTypes';
import { useHideProposalMutation } from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { validationErrors } from '../assessmentView';
import { TextField } from '../components/FormParts';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = { surveyId: number; proposal: Proposal; onClose: () => void };

/** Proposals are anonymous, but the whole team sees them. One that points to a person is hidden. */
export const HideProposalDialog = ({ surveyId, proposal, onClose }: Props): ReactElement => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const hide = useHideProposalMutation(surveyId);
  const invalid = Boolean(validationErrors(hide.error));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    hide.mutate({ id: proposal.id, reason }, { onSuccess: onClose });
  };

  return (
    <Dialog open onClose={onClose} closedby="any" closeButton={t('workEnvironmentRisk.data.close')}>
      <form onSubmit={submit} noValidate className={classes.areaBody}>
        <Heading level={2} data-size="xs">
          {t('workEnvironmentMeasures.hide.title', { title: proposal.title })}
        </Heading>
        <Paragraph data-size="sm">{t('workEnvironmentMeasures.hide.text')}</Paragraph>
        <TextField
          id="hide-reason"
          label={t('workEnvironmentMeasures.hide.reason')}
          value={reason}
          error={invalid ? t('workEnvironmentMeasures.hide.reasonError') : undefined}
          onChange={setReason}
        />
        {hide.isError && !invalid && (
          <Alert data-color="danger">
            <Paragraph>{t('workEnvironmentMeasures.hide.failed')}</Paragraph>
          </Alert>
        )}
        <div className={classes.dialogButtons}>
          <Button type="submit" variant="primary" data-size="sm" loading={hide.isPending}>
            {t('workEnvironmentMeasures.hide.submit')}
          </Button>
          <Button type="button" variant="tertiary" data-size="sm" onClick={onClose}>
            {t('workEnvironmentRisk.assessment.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
